import React, { useMemo } from 'react'
import { BrandPlanIntegrations, SelectedBlogPlatform, SelectedEmailPlatform } from 'types'
import {
  EMAIL_TACTICS,
  SEO_TACTICS,
  SOCIAL_MEDIA_TACTICS,
  VIDEO_TACTICS,
} from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTactics'
import { TacticIntegration } from './TacticIntegration/TacticIntegration'
import Styles from './TacticIntegrationList.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { getFirstUnconnectedIntegrationIndex, TacticInfo, INTEGRATION_INFO } from './utils'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { Timeline } from 'blaze-ui/icons'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

// Index starts at 1 for Google, then 2 for the first social media tactic, etc.
const START_INDEX = 2

export type TacticIntegrationListVariant = 'onboarding' | 'integrations_page'

interface TacticIntegrationListProps {
  tactics: BrandPlanIntegrations[]
  selectedBlogPlatform?: SelectedBlogPlatform
  selectedEmailPlatform?: SelectedEmailPlatform
  variant?: TacticIntegrationListVariant
}

interface TacticIntegrationSectionProps {
  title: string
  tactics?: BrandPlanIntegrations[]
  tacticInfos?: TacticInfo[]
  startIndex: number
  selectedBlogPlatform?: SelectedBlogPlatform
  selectedEmailPlatform?: SelectedEmailPlatform
  firstUnconnectedIntegrationIndex?: number
  variant?: TacticIntegrationListVariant
}

const TacticIntegrationSection: React.FC<TacticIntegrationSectionProps> = ({
  title,
  tactics,
  tacticInfos,
  startIndex,
  selectedBlogPlatform,
  selectedEmailPlatform,
  firstUnconnectedIntegrationIndex,
  variant = 'onboarding',
}) => {
  const headingLevel = variant === 'integrations_page' ? 4 : 3

  // If tacticInfos are provided, use them directly
  if (tacticInfos) {
    if (tacticInfos.length === 0) {
      return null
    }

    return (
      <div className={Styles.tacticSection}>
        <Heading level={headingLevel}>{title}</Heading>
        <div className={Styles.tacticList}>
          {tacticInfos.map((tacticInfo, i) => {
            const index = startIndex + i
            return (
              <TacticIntegration
                key={tacticInfo.integrationType}
                tacticInfo={tacticInfo}
                index={index}
                isHighlighted={firstUnconnectedIntegrationIndex === index}
              />
            )
          })}
        </div>
      </div>
    )
  }

  // Otherwise, use tactics (existing behavior)
  if (!tactics || tactics.length === 0) {
    return null
  }

  return (
    <div className={Styles.tacticSection}>
      <Heading level={headingLevel}>{title}</Heading>
      <div className={Styles.tacticList}>
        {tactics.map((tactic, i) => {
          const index = startIndex + i
          return (
            <TacticIntegration
              key={String(tactic)}
              tactic={tactic}
              index={index}
              selectedBlogPlatform={selectedBlogPlatform}
              selectedEmailPlatform={selectedEmailPlatform}
              isHighlighted={firstUnconnectedIntegrationIndex === index}
              variant={variant}
            />
          )
        })}
      </div>
    </div>
  )
}

export const TacticIntegrationList: React.FC<TacticIntegrationListProps> = ({
  tactics,
  selectedBlogPlatform,
  selectedEmailPlatform,
  variant = 'onboarding',
}) => {
  const { data } = useExternalIntegrations()

  const socialMediaTactics = SOCIAL_MEDIA_TACTICS.filter((tactic) => tactics.includes(tactic))
  const videoTactics = VIDEO_TACTICS.filter((tactic) => tactics.includes(tactic))
  const seoTactics = SEO_TACTICS.filter((tactic) => tactics.includes(tactic))
  const emailTactics = EMAIL_TACTICS.filter((tactic) => tactics.includes(tactic))

  const firstUnconnectedIntegrationIndex = useMemo(() => {
    const externalIntegrations = data?.external_integrations || []
    return getFirstUnconnectedIntegrationIndex(
      tactics,
      externalIntegrations,
      selectedBlogPlatform,
      selectedEmailPlatform,
    )
  }, [tactics, data, selectedBlogPlatform, selectedEmailPlatform])

  const allSocialTactics = [...socialMediaTactics, ...videoTactics]

  // Check if Meta Ads is connected
  const isMetaAdsConnected = useMemo(() => {
    return (
      data?.external_integrations?.some((integration) => integration.provider === ExternalIntegrationEnum.MetaAds) ??
      false
    )
  }, [data?.external_integrations])

  // Check if Google Drive is connected
  const isGoogleDriveConnected = useMemo(() => {
    return (
      data?.external_integrations?.some(
        (integration) => integration.provider === ExternalIntegrationEnum.GoogleDrive,
      ) ?? false
    )
  }, [data?.external_integrations])

  // Adjust start index based on whether Meta Ads is shown (at index 2)
  // If both Meta Ads and Google Drive are connected, Google Drive would be at index 3
  let currentIndex = START_INDEX
  if (isMetaAdsConnected) {
    currentIndex += 1
  }
  if (isGoogleDriveConnected) {
    currentIndex += 1
  }
  const socialMediaStartIndex = currentIndex

  const brandPlanSections = (
    <>
      <TacticIntegrationSection
        title="Social Media"
        tactics={allSocialTactics}
        startIndex={socialMediaStartIndex}
        firstUnconnectedIntegrationIndex={firstUnconnectedIntegrationIndex}
        variant={variant}
      />
      <TacticIntegrationSection
        title="Blog"
        tactics={seoTactics}
        startIndex={allSocialTactics.length + socialMediaStartIndex}
        selectedBlogPlatform={selectedBlogPlatform}
        firstUnconnectedIntegrationIndex={firstUnconnectedIntegrationIndex}
        variant={variant}
      />
      <TacticIntegrationSection
        title="Email"
        tactics={emailTactics}
        startIndex={allSocialTactics.length + seoTactics.length + socialMediaStartIndex}
        firstUnconnectedIntegrationIndex={firstUnconnectedIntegrationIndex}
        selectedEmailPlatform={selectedEmailPlatform}
        variant={variant}
      />
    </>
  )

  return (
    <>
      <TacticIntegrationSection
        title="Website Traffic"
        tactics={['google']}
        startIndex={1}
        firstUnconnectedIntegrationIndex={firstUnconnectedIntegrationIndex}
        variant="onboarding"
      />
      {isMetaAdsConnected && (
        <TacticIntegrationSection
          title="Ads"
          tacticInfos={[INTEGRATION_INFO.META_ADS]}
          startIndex={2}
          firstUnconnectedIntegrationIndex={firstUnconnectedIntegrationIndex}
          variant="onboarding"
        />
      )}
      {isGoogleDriveConnected && (
        <TacticIntegrationSection
          title="Other"
          tacticInfos={[INTEGRATION_INFO.GOOGLE_DRIVE]}
          startIndex={isMetaAdsConnected ? 3 : 2}
          firstUnconnectedIntegrationIndex={firstUnconnectedIntegrationIndex}
          variant="onboarding"
        />
      )}
      {variant === 'integrations_page' ? (
        <div className={Styles.brandPlanSection}>
          <div className={Styles.brandPlanHeader}>
            <Timeline size={24} color="var(--magenta-50)" />
            <Heading level={3}>In your Content Plan</Heading>
          </div>
          <div className={Styles.brandPlanSubtitle}>
            <Text>Autopilot will create and autopost content for these channels</Text>
          </div>
          <div className={Styles.sectionContainer}>{brandPlanSections}</div>
        </div>
      ) : (
        brandPlanSections
      )}
    </>
  )
}
